import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import WarningIcon from "@mui/icons-material/Warning";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import FeedbackIcon from "@mui/icons-material/Feedback";

// Styled components for dialog, title, content, and actions
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(96, 63, 151, 0.15)",
    maxWidth: 600,
    width: "100%",
    margin: 16,
    background: theme.palette.background.paper,
  },
}));

const getTitleBgColor = (theme, type) => {
  if (type === "info") return theme.palette.info.main;
  if (type === "error") return theme.palette.error.main;
  if (type === "warning") return theme.palette.warning.main;
  return `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
};

export const StyledDialogTitle = styled(DialogTitle, {
  shouldForwardProp: (prop) => prop !== "type",
})(({ theme, type }) => ({
  background: getTitleBgColor(theme, type),
  color: theme.palette.primary.contrastText,
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontWeight: 600,
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "24px",
  background: theme.palette.background.paper,
}));

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
}));

export const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "type",
})(({ theme, type }) => ({
  borderRadius: 8,
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 500,
  minWidth: 100,
  ...(type === "warning" && {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  }),
  ...(type === "error" && {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  }),
  ...(type === "info" && {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.info.dark,
    },
  }),
}));

export const CustomDialogBase = ({
  open,
  onClose,
  title,
  type = "info",
  loading = false,
  actions, // optional custom actions
  children, // free content
}) => {
  return (
    <StyledDialog open={open} onClose={onClose} fullWidth>
      <StyledDialogTitle type={type}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box display='flex' alignItems='center' gap={1}>
            {type === "warning" && (
              <WarningIcon color='white' sx={{ fontSize: 24, mt: "-2px" }} />
            )}
            {type === "error" && (
              <PriorityHighRoundedIcon
                color='white'
                sx={{ fontSize: 24, mt: "-2px" }}
              />
            )}
            {type === "info" && (
              <FeedbackIcon color='white' sx={{ fontSize: 24, mt: "-2px" }} />
            )}
            {title && (
              <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{ color: "inherit", ml: 2 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <br />
        {children}
      </StyledDialogContent>

      {actions && (
        <StyledDialogActions sx={{ justifyContent: "flex-end" }}>
          {actions}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

const CustomDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  type = "info",
  loading = false,
}) => {
  return (
    <StyledDialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <StyledDialogTitle type={type}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box display='flex' alignItems='center' gap={1}>
            {type === "warning" && (
              <WarningIcon
                color='white'
                sx={{ fontSize: 24, mt: "-2px" }} // vertical align tweak
              />
            )}
            {type === "error" && (
              <PriorityHighRoundedIcon
                color='white'
                sx={{ fontSize: 24, mt: "-2px" }} // vertical align tweak
              />
            )}
            <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{ color: "inherit", ml: 2 }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <br />
        <Typography
          color='text.secondary'
          sx={{ fontSize: 16, mb: 1, textAlign: "justify" }}
        >
          {description}
        </Typography>
      </StyledDialogContent>
      <StyledDialogActions sx={{ justifyContent: "flex-end" }}>
        <ActionButton
          onClick={onClose}
          color='secondary'
          variant='outlined'
          sx={{
            borderColor: "grey.400",
            color: "grey.700",
            "&:hover": { borderColor: "grey.600", backgroundColor: "grey.100" },
          }}
          disabled={loading}
        >
          Cancel
        </ActionButton>
        <ActionButton
          onClick={onConfirm}
          variant='contained'
          type={type}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} color='inherit' sx={{ mr: 1 }} />
          ) : null}
          Confirm
        </ActionButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default CustomDialog;
